import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Landmark, Printer, Undo2 } from 'lucide-react'
import { useState, type ReactNode } from 'react'

import type { CommissionLine, CommissionSellerRow } from '@aeris/contracts'
import { parseAmount } from '@aeris/finance'
import { formatMoney } from '@aeris/money'
import { commissionStatementFor, rateLabel } from '@aeris/printing'
import {
  Badge,
  Button,
  ConfirmDialog,
  InlineAlert,
  Sheet,
  SheetContent,
  Spinner,
} from '@aeris/ui'

import {
  fetchCommissionSeller,
  periodLabel,
  periodShort,
  voidCommissionStatement,
} from '../lib/api/commissions.ts'
import { ApiError } from '../lib/api.ts'
import { useAuth } from '../lib/auth-context.ts'
import { figuresOf } from '../lib/commission-figures.ts'
import { describeCommissionLine, itemLabel } from '../lib/commission-lines.ts'
import { statementInputOf } from '../lib/commission-report.ts'
import { formatDate } from '../lib/date-format.ts'
import { useWorkspace } from '../lib/workspace.ts'

import { PrintDocumentDialog } from './print-document-dialog.tsx'

/**
 * As vendas que compõem a comissão de um vendedor no mês (F137).
 *
 * A resposta a "de onde saiu esse valor?": cada venda que somou, cada troca e
 * cancelamento que descontou, com a % de cada uma. No mês fechado as linhas são
 * as do extrato — congeladas — e o que chegou depois aparece à parte, "para o
 * próximo fechamento".
 */

const money = (wire: string): string => formatMoney(parseAmount(wire))

const Lines = ({
  lines,
  label,
}: {
  lines: readonly CommissionLine[]
  label: string
}): ReactNode => (
  <ul
    aria-label={label}
    className="flex flex-col divide-y divide-(--border-subtle)"
  >
    {lines.map((line) => (
      <li
        key={`${line.sourceType}-${line.sourceId}`}
        className="flex items-start justify-between gap-3 py-2 text-(length:--text-sm)"
      >
        <span className="flex min-w-0 flex-col">
          <span className="truncate">{describeCommissionLine(line)}</span>
          {/*
            O que foi vendido (F160). O gerente: "tinha que aparecer o que foi
            vendido" — sem isto, conferir o extrato exigia abrir cada venda.
          */}
          {line.items.length === 0 ? null : (
            <span className="text-(length:--text-xs) text-(--text-secondary)">
              {line.items.map(itemLabel).join(' · ')}
            </span>
          )}
          <span className="flex flex-wrap items-center gap-1 text-(length:--text-xs) text-(--text-muted)">
            {`${formatDate(line.occurredAt)} · ${rateLabel(line.rate)}`}
            {line.fromEarlierPeriod ? (
              <Badge tone="warning">de mês anterior</Badge>
            ) : null}
          </span>
        </span>
        <span
          className={
            line.baseAmount.startsWith('-')
              ? 'shrink-0 font-(--font-mono) tabular-nums text-(--danger)'
              : 'shrink-0 font-(--font-mono) tabular-nums'
          }
        >
          {money(line.baseAmount)}
        </span>
      </li>
    ))}
  </ul>
)

export const CommissionSellerPanel = ({
  period,
  row,
  canManage,
  onClose,
}: {
  period: string
  row: CommissionSellerRow
  canManage: boolean
  onClose: () => void
}): ReactNode => {
  const { session } = useAuth()
  const workspace = useWorkspace()
  const queryClient = useQueryClient()
  const [printing, setPrinting] = useState(false)
  const [undoing, setUndoing] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const detail = useQuery({
    queryKey: ['commissions', 'seller', row.userId, period],
    queryFn: ({ signal }) => fetchCommissionSeller(row.userId, period, signal),
  })

  const statement = row.statement
  const figures = figuresOf(row)

  const undo = useMutation({
    mutationFn: async (reason: string) => {
      if (statement === null) return
      await voidCommissionStatement(statement.id, {
        version: statement.version,
        reason,
      })
    },
    onSuccess: async () => {
      setUndoing(false)
      await queryClient.invalidateQueries({ queryKey: ['commissions'] })
      void queryClient.invalidateQueries({ queryKey: ['finance'] })
    },
    onError: (cause: unknown) => {
      setUndoing(false)
      setError(
        cause instanceof ApiError
          ? cause.message
          : 'Não foi possível desfazer o fechamento.'
      )
    },
  })

  const lines = detail.data?.lines ?? []
  const pendingLines = detail.data?.pendingLines ?? []

  const printable = commissionStatementFor(
    statementInputOf({
      companyName:
        session?.context?.companyTradeName ??
        session?.context?.companyName ??
        'Loja',
      period: periodShort(period),
      row,
      lines,
    })
  )

  return (
    <Sheet
      open
      onOpenChange={(open) => {
        if (!open) onClose()
      }}
    >
      <SheetContent
        title={`Comissão de ${row.name}`}
        side="right"
        className="w-full max-w-lg"
      >
        <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto p-4">
          <div className="flex flex-col gap-1">
            <span className="text-(length:--text-sm) capitalize text-(--text-secondary)">
              {periodLabel(period)}
            </span>
            <span className="font-(--font-mono) text-(length:--text-2xl) font-(--weight-semibold) tabular-nums">
              {money(figures.commissionAmount)}
            </span>
            <span className="text-(length:--text-sm) text-(--text-secondary)">
              {`base ${money(figures.baseAmount)}`}
              {statement === null
                ? ' · prévia'
                : ` · fechado em ${formatDate(statement.closedAt)}${statement.closedByName === null ? '' : ` por ${statement.closedByName}`}`}
            </span>
            {statement?.financeStatus === 'settled' ? (
              <Badge tone="success" className="self-start">
                Pago
              </Badge>
            ) : null}
          </div>

          <div className="flex flex-wrap gap-2">
            <Button
              variant="secondary"
              size="sm"
              leadingIcon={<Printer aria-hidden="true" className="h-4 w-4" />}
              disabled={detail.data === undefined}
              onClick={() => {
                setPrinting(true)
              }}
            >
              Imprimir extrato
            </Button>
            {statement?.financeEntryId == null ? null : (
              <Button
                variant="ghost"
                size="sm"
                leadingIcon={
                  <Landmark aria-hidden="true" className="h-4 w-4" />
                }
                onClick={() => {
                  workspace.open({ module: 'financeiro' })
                }}
              >
                Ver no Financeiro
              </Button>
            )}
            {canManage &&
            statement !== null &&
            statement.financeStatus !== 'settled' ? (
              <Button
                variant="ghost"
                size="sm"
                leadingIcon={<Undo2 aria-hidden="true" className="h-4 w-4" />}
                onClick={() => {
                  setError(null)
                  setUndoing(true)
                }}
              >
                Desfazer fechamento
              </Button>
            ) : null}
          </div>

          {error === null ? null : (
            <InlineAlert tone="danger">{error}</InlineAlert>
          )}

          {detail.isError ? (
            <InlineAlert tone="danger">
              Não foi possível carregar as vendas deste vendedor.
            </InlineAlert>
          ) : detail.isPending ? (
            <div className="flex justify-center py-6">
              <Spinner />
            </div>
          ) : (
            <>
              <section className="flex flex-col gap-1">
                <h3 className="text-(length:--text-xs) font-(--weight-semibold) uppercase tracking-wide text-(--text-muted)">
                  {statement === null ? 'Entra no fechamento' : 'No extrato'}
                </h3>
                {lines.length === 0 ? (
                  <p className="text-(length:--text-sm) text-(--text-muted)">
                    Nenhuma venda com comissão.
                  </p>
                ) : (
                  <Lines
                    lines={lines}
                    label={
                      statement === null ? 'Entra no fechamento' : 'No extrato'
                    }
                  />
                )}
              </section>

              {pendingLines.length === 0 ? null : (
                <section className="flex flex-col gap-1">
                  <h3 className="text-(length:--text-xs) font-(--weight-semibold) uppercase tracking-wide text-(--text-muted)">
                    Para o próximo fechamento
                  </h3>
                  <p className="text-(length:--text-xs) text-(--text-muted)">
                    Chegou depois de este mês ser fechado: um pedido entregue,
                    uma troca ou um cancelamento.
                  </p>
                  <Lines
                    lines={pendingLines}
                    label="Para o próximo fechamento"
                  />
                </section>
              )}
            </>
          )}
        </div>

        {printing ? (
          <PrintDocumentDialog
            document={printable}
            description={`Extrato de comissão de ${row.name}`}
            initialPaper="a4"
            onClose={() => {
              setPrinting(false)
            }}
          />
        ) : null}

        <ConfirmDialog
          open={undoing}
          onOpenChange={(open) => {
            if (!open) setUndoing(false)
          }}
          title={`Desfazer o fechamento de ${row.name}?`}
          description="O título a pagar é cancelado e as vendas voltam para a prévia. Dá para fechar de novo depois."
          confirmLabel="Desfazer fechamento"
          cancelLabel="Voltar"
          reason={{
            label: 'Motivo',
            required: true,
            placeholder: 'ex.: a % estava errada',
          }}
          loading={undo.isPending}
          onConfirm={(reason) => {
            undo.mutate((reason ?? '').trim())
          }}
        />
      </SheetContent>
    </Sheet>
  )
}
